import { Component, 
  OnInit, 
  OnDestroy,
  Output, 
  EventEmitter, 
  ChangeDetectorRef, 
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  SaveDataItem,
  ClobSettingsObj,
  FilterParams
} from '../f1065-k2-data-entry.service';

// Extended interface for P7S1 with COL_DESC_INFO and PREF validation
interface P7S1FormLineItem extends FormLineItem {
  COL_DESC_INFO?: string;
  IS_PREF_MANDATORY?: string;
  PREF_MAN_COLS?: string[];
  VALIDATION_MSG?: string;
}

interface P7S1ColumnHeaderData extends ColumnHeaderData {
  COL_DESC_INFO?: string;
  IS_PREF_MANDATORY?: string;
  PREF_MAN_COLS?: string[];
  VALIDATION_MSG?: string;
}

@Component({
    selector: 'gtw-f1065-k2-p7s1',
    templateUrl: './f1065-k2-p7s1.component.html',
    styleUrls: ['./f1065-k2-p7s1.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1065K2P7S1Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  // ============================================
  // OUTPUT EVENTS
  // ============================================
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ============================================
  // INPUT PROPERTIES (from AngularJS)
  // ============================================
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};

  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // ============================================
  // COMPONENT STATE
  // ============================================
  
  loadingData = true;
  showLoader = true;

  colHeaderData: P7S1ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];

  totalDropDownActions = 0;
  loadedActionCounter = 0;

  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isValidationError = false;

  currentCodeGroup = '';
  formSchd = '';
  currentEntityName = '';
  currentPartnerName = '';
  scenario = '';
  jcdKey = 0;

  // P7S1 specific: Section header
  sectionHeader = 'Section 1 - General Information on Passive Foreign Investment Company (PFIC), Qualified Electing Fund (QEF), or Qualifying Insurance Corporation (QIC):';

  // P7S1 specific: Action code for line details
  lineDetailsActionCode = 'w5bepp';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
      this.cdr.markForCheck();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;

    const hasGlobalService = !!this.globalService && Object.keys(this.globalService).length > 0;
    const hasBaseUrl = !!this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = !!this.filterParams && Object.keys(this.filterParams).length > 0;

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams) return;

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) return;

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    console.log('[F1065K2-P7S1] All data present, initializing!');
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    console.log('[F1065K2-P7S1] init() started');
    try {
      this.scenario = this.filterParams.scenario || '';
      this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;

      this.checkLockStatus();
      this.setCurrentCodeGroup();
      this.loadColumnHeaders();
    } catch (e) {
      console.error('[F1065K2-P7S1] Error in init:', e);
      this.loadingData = false;
      this.showLoader = false;
      this.cdr.detectChanges();
    }
  }

  private reloadData(): void {
    console.log('[F1065K2-P7S1] reloadData() called');
    
    this.isLoading = false;
    this.lineData = [];
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();

    this.scenario = this.filterParams.scenario || '';
    this.jcdKey = this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
    
    this.setCurrentCodeGroup();
    this.checkLockStatus();
    this.loadColumnHeaders();
  }

  // P7S1 specific: linkPart = '_P7_S1'
  private setCurrentCodeGroup(): void {
    const form = this.filterParams.p_form || 'F1065';
    const schedule = this.filterParams.p_schedule || 'K2';
    const linkPart = '_P7_S1';
    
    this.currentCodeGroup = `${form}_${schedule}${linkPart}`;
    this.formSchd = `${form}_${schedule}`;
  }

  private checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url) return;

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
        this.cdr.detectChanges();
      },
      (error) => console.error('Error checking lock status:', error)
    );
  }

  // ============================================
  // DATA LOADING
  // ============================================

  private loadColumnHeaders(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;

    this.service.getColumnHeaders(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        this.colHeaderData = data || [];
        
        if (!this.colHeaderData || this.colHeaderData.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        let hasDropdowns = false;
        
        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          
          if (col.DROP_DOWN_ACTION && col.DROP_DOWN_ACTION.trim() !== '') {
            hasDropdowns = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }

        if (!hasDropdowns) {
          this.loadLineDetails();
        }
      },
      (error) => {
        console.error('Error loading column headers:', error);
        this.service.showError('Error loading form configuration');
        this.loadingData = false;
        this.showLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadDropdownData(colData: P7S1ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');
    const actionCode = actionDetails[0];
    const codeGroup = actionDetails[1] || '';

    this.service.getDropdownData(
      this.GENERAL_CONFIG.base_url,
      actionCode,
      codeGroup,
      this.filterParams.tax_year || '',
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        colData.actionData = data || [];
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading dropdown data:', error);
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
      }
    );
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const filerKey = this.filterParams.p_filer_key;
    const hasFilerKey = filerKey !== undefined && filerKey !== null && filerKey !== '' && 
                        (typeof filerKey === 'string' ? filerKey.trim().length > 0 : filerKey >= 0);
    
    if (!hasFilerKey) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const partnerKey = this.filterParams.p_filer_partner_key;
    const hasPartnerKey = partnerKey !== undefined && partnerKey !== null && partnerKey !== '' &&
                          (typeof partnerKey === 'string' ? partnerKey.trim().length > 0 : (partnerKey as number) >= 0);
    
    if (this.currentCodeGroup.includes('_K3') && !hasPartnerKey) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (this.currentCodeGroup.includes('_K3')) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    this.service.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      filerKey!,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);

        // P7S1 specific: calcTotalsCol is EMPTY - no totals calculation
        // (Just calling it for consistency but it does nothing)
        for (const line of this.groupedFormLines) {
          this.calcTotalsCol(line.items);
        }

        this.showLoader = false;
        this.loadingData = false;
        this.showErrCol = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error loading line details:', error);
        this.service.showError('Error loading form data');
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ============================================
  // DATA PROCESSING
  // ============================================

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (!formLines || formLines.length === 0) return [];

    this.isAdmin = true;

    return _.reduce(
      formLines,
      (memo: GroupedFormLine[], property: any) => {
        let bucket = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESC: property.LINE_DESC,
            LINE_ORDER: property.LINE_ORDER,
            IS_DISPLAY: 'Y',
            SNO: property.SNO,
            IS_LINE_MULTI: property.IS_LINE_MULTI,
            items: []
          };
          memo.push(bucket);
        }

        // P7S1 specific: includes COL_DESC_INFO
        const attribs: P7S1FormLineItem = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESC: property.COL_DESC,
          COL_DESC_INFO: property.COL_DESC_INFO,  // P7S1 specific
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.EDITABLE_Y_N,
          SNO: property.SNO,
          ADJ_KEY: property.ADJ_KEY,
          IS_COL_MULTI: property.IS_COL_MULTI,
          UI_COMP: property.UI_COMP,
          ACTION_Y_N: property.ACTION_Y_N,
          IS_MANDATORY: 'N',
          CELL_VALUE: property.CELL_VALUE
        };

        if (attribs.ACTION_Y_N === 'Y') {
          const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
          if (colObj) {
            attribs.actionData = colObj.actionData;
          }
        }

        this.setValidationInfo(property, attribs);

        if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = undefined;
        }

        if (property.IS_LINE_MULTI === 'Y' && this.isEmptyVal(attribs.CELL_VALUE)) {
          this.setUICompDefaults(attribs);
        }

        if (attribs.UI_COMP === 'DATE' && this.isUICompNotEmpty(attribs)) {
          const parsedDate = parseAsLocalDate(property.CELL_VALUE);
          if (parsedDate) {
            attribs.CELL_VALUE = this.formatDateForInput(parsedDate);
          }
        }

        if (this.isSkipFromSaving(bucket, attribs)) {
          attribs.OPERATION_TYPE = 'N';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  // P7S1 specific: Extended setValidationInfo with PREF_REQ support
  private setValidationInfo(source: any, dest: any): void {
    if (source.VALIDATION_RULE) {
      const rules = source.VALIDATION_RULE.split(';');
      for (const rule of rules) {
        if (rule === 'REQ') {
          dest.IS_MANDATORY = 'Y';
        } else if (rule.startsWith('PREF_REQ')) {
          // P7S1 specific: PREF_REQ validation
          dest.IS_PREF_MANDATORY = 'Y';
          if (rule.includes(':') && rule.includes('/')) {
            dest.PREF_MAN_COLS = rule.split(':')[1].split('/');
            dest.VALIDATION_MSG = dest.PREF_MAN_COLS[dest.PREF_MAN_COLS.length - 1];
            dest.PREF_MAN_COLS.splice(dest.PREF_MAN_COLS.length - 1, 1);
          }
        } else if (rule.includes('MAX_LEN:')) {
          dest.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private setUICompDefaults(attrib: FormLineItem): void {
    if (attrib.UI_COMP === 'NUMERIC') {
      attrib.CELL_VALUE = null;
    } else if (attrib.UI_COMP === 'TEXT' || attrib.UI_COMP === 'DATE' || 
               attrib.UI_COMP === 'CHK_BX' || attrib.UI_COMP === 'DROP_DOWN') {
      attrib.CELL_VALUE = '';
    }
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  private isUICompEmpty(attrib: FormLineItem): boolean {
    if (attrib.CELL_VALUE === undefined || attrib.CELL_VALUE === null) return true;
    if (attrib.UI_COMP === 'NUMERIC' && attrib.CELL_VALUE === null) return true;
    if ((attrib.UI_COMP === 'TEXT' || attrib.UI_COMP === 'DATE' || 
         attrib.UI_COMP === 'CHK_BX' || attrib.UI_COMP === 'DROP_DOWN') && 
        attrib.CELL_VALUE === '') return true;
    return false;
  }

  private isUICompNotEmpty(attrib: FormLineItem): boolean {
    return !this.isUICompEmpty(attrib);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for save - YYYY-M-D format (without leading zeros)
   * Matches original AngularJS: tempDate.getFullYear() + '-' + (tempDate.getMonth() + 1) + '-' + tempDate.getDate()
   * Handles both Date objects and string inputs
   */
  private formatDateForSave(dateValue: string | Date | null): string {
    if (!dateValue) return '';
    
    // If it's already a Date object, format directly
    if (dateValue instanceof Date) {
      return `${dateValue.getFullYear()}-${dateValue.getMonth() + 1}-${dateValue.getDate()}`;
    }
    
    // If it's a string, parse and format
    const date = parseAsLocalDate(dateValue);
    if (date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
    
    return String(dateValue);
  }

  // ============================================
  // ROW OPERATIONS
  // ============================================

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const tempObj = _.cloneDeep(selectedRowData);
    
    const multiRow = this.groupedFormLines.filter(
      line => line.LINE_KEY === selectedRowData.LINE_KEY && line.IS_DISPLAY === 'Y'
    );
    const sno = multiRow[multiRow.length - 1].SNO + 1;

    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      this.setUICompDefaults(item);
      item.SNO = sno;
    }

    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
    this.cdr.detectChanges();
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;

    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }

    if (!isRowSaved) {
      const index = this.groupedFormLines.indexOf(selectedRowData);
      this.groupedFormLines.splice(index, 1);
    }

    this.cdr.detectChanges();
  }

  // ============================================
  // CALCULATIONS - P7S1 SPECIFIC: EMPTY FUNCTION
  // ============================================

  // P7S1 does NOT calculate totals - this function intentionally does nothing
  calcTotalsCol(columnValues: FormLineItem[]): void {
    // Empty implementation for P7S1
  }

  onNumericChange(items: FormLineItem[]): void {
    // No totals calculation for P7S1
    this.cdr.detectChanges();
  }

  /**
   * Handle date input change - uses parseAsLocalDate to avoid timezone shift
   */
  onDateChange(item: FormLineItem, dateString: string): void {
    if (dateString) {
      item.CELL_VALUE = parseAsLocalDate(dateString);
    } else {
      item.CELL_VALUE = '';
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  // ============================================
  // VALIDATION - P7S1 SPECIFIC: PREF_MANDATORY
  // ============================================

  private validateForm(): boolean {
    this.isValidationError = false;
    let requiredMsgAlreadyAdded = false;

    for (const line of this.groupedFormLines) {
      line.ERR_MSG = undefined;
      requiredMsgAlreadyAdded = false;

      // Reset error flags
      for (const item of line.items) {
        (item as P7S1FormLineItem).IS_ERROR = 'N';
      }

      for (const item of line.items) {
        const p7s1Item = item as P7S1FormLineItem;

        if (line.IS_DISPLAY === 'Y' && item.IS_ENABLED !== 'N') {
          // Standard mandatory validation
          if (item.IS_MANDATORY === 'Y' && 
              this.isUICompEmpty(item) && 
              this.isAnyOtherValueEntered(item, line.items)) {
            this.setErrorAttribs(line, p7s1Item, 'Mandatory field(s) are missing', requiredMsgAlreadyAdded);
            requiredMsgAlreadyAdded = true;
          }

          // P7S1 specific: PREF_MANDATORY validation
          this.validatePrefMandatoryCols(line, p7s1Item);

          // P7S1 specific: Date range validation for columns starting with '(d).'
          if (item.UI_COMP === 'DATE' && item.COL_DESC?.startsWith('(d).')) {
            const items = line.items as P7S1FormLineItem[];
            const currentIndex = items.indexOf(p7s1Item);
            const nextItem = items[currentIndex + 1];
            
            if (item.CELL_VALUE && nextItem?.CELL_VALUE) {
              const startDate = new Date(item.CELL_VALUE);
              const endDate = new Date(nextItem.CELL_VALUE);
              
              if (startDate > endDate) {
                this.setErrorAttribs(line, p7s1Item, 
                  "'(d). Beginning of PFIC tax year' should be earlier than '(e). Ending of PFIC tax year'", 
                  false);
              }
            }
          }
        }
      }
    }

    if (this.isValidationError) {
      this.showErrCol = true;
    }

    this.cdr.detectChanges();
    return !this.isValidationError;
  }

  // P7S1 specific: PREF_MANDATORY columns validation
  private validatePrefMandatoryCols(line: GroupedFormLine, currentCell: P7S1FormLineItem): void {
    if (currentCell.IS_PREF_MANDATORY !== 'Y' || !currentCell.PREF_MAN_COLS || currentCell.PREF_MAN_COLS.length === 0) {
      return;
    }

    const items = line.items as P7S1FormLineItem[];
    const tempArr = ['', ''];

    const firstSetOfCols = items.filter(col => col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![0]));
    const secondSetOfCols = items.filter(col => col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![1]));
    
    tempArr[0] = firstSetOfCols.map(val => val.CELL_VALUE || '').join('');
    tempArr[1] = secondSetOfCols.map(val => val.CELL_VALUE || '').join('');

    if (tempArr[0].trim().length <= 0 && tempArr[1].trim().length <= 0) {
      // Both empty
      this.setErrorAttribs(line, null as any, currentCell.VALIDATION_MSG || 'Please fill required fields', false);
      
      for (const col of items) {
        if (col.IS_PREF_MANDATORY === 'Y') {
          if (col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![0]) ||
              col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![1])) {
            col.IS_ERROR = 'Y';
          }
        }
      }
    } else if (tempArr[0].trim().length > 0 && tempArr[1].trim().length > 0) {
      // Both filled - error
      this.setErrorAttribs(line, null as any, currentCell.VALIDATION_MSG || 'Please fill only one set of fields', false);
      
      for (const col of items) {
        if (col.IS_PREF_MANDATORY === 'Y') {
          if ((col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![0]) && this.isUICompNotEmpty(col)) ||
              (col.COL_DESC_INFO?.startsWith(currentCell.PREF_MAN_COLS![1]) && this.isUICompNotEmpty(col))) {
            col.IS_ERROR = 'Y';
          }
        }
      }
    } else {
      // Check one set of PREF_MANDATORY fields are filled
      let count = 0;
      const colsToCheck = tempArr[0].trim().length > 0 ? 
        currentCell.PREF_MAN_COLS![0] : currentCell.PREF_MAN_COLS![1];
      
      for (const col of items) {
        if (col.IS_PREF_MANDATORY === 'Y' && 
            col.COL_DESC_INFO?.startsWith(colsToCheck) && 
            this.isUICompEmpty(col)) {
          col.IS_ERROR = 'Y';
          if (count === 0) {
            this.setErrorAttribs(line, null as any, currentCell.VALIDATION_MSG || 'Please complete all required fields', false);
            count = 1;
          }
        }
      }
    }
  }

  private isAnyOtherValueEntered(item: FormLineItem, itemList: FormLineItem[]): boolean {
    return itemList.some(x => x !== item && x.IS_ENABLED === 'Y' && this.isUICompNotEmpty(x));
  }

  private setErrorAttribs(line: GroupedFormLine, col: P7S1FormLineItem | null, errMsg: string, alreadyAdded: boolean): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
    }
    if (col) {
      col.IS_ERROR = 'Y';
    }
    this.isValidationError = true;
    
    if (!alreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', `<li>${errMsg}</li></ol>`);
    }
  }

  // ============================================
  // SAVE & RESET
  // ============================================

  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.isInitialized = false;
    this.isLoading = false;
    this.loadColumnHeaders();
  }

  save(): void {
    if (this.isSaveClicked) {
      this.service.showInfo('Please wait while we save this request');
      return;
    }

    if (!this.validateForm()) {
      this.service.showError('Please fix all error(s)');
      return;
    }

    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onSaveConfirm(): void {
    this.showConfirmModal = false;
    this.performSave();
  }

  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  private performSave(): void {
    this.isSaveClicked = true;

    const clobSettings: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    const dataToSave = this.buildDataToSave();

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.service.saveData(
          this.GENERAL_CONFIG.base_url,
          clobSettings,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (result) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Error saving data';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
              this.requestGridUpdate.emit('refresh');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
            this.cdr.detectChanges();
          },
          (error) => {
            console.error('Error saving data:', error);
            this.service.showError('Error occurred while saving. Please try again.');
            this.isSaveClicked = false;
            this.cdr.detectChanges();
          }
        );
      },
      (error) => {
        console.error('Error checking lock status:', error);
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  private buildDataToSave(): SaveDataItem[] {
    const dataToSave: SaveDataItem[] = [];

    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        const returnObj: SaveDataItem = {
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          ADJ_KEY: item.ADJ_KEY,
          SNO: item.SNO,
          CELL_VALUE: null,
          OPERATION_TYPE: ''
        };

        if (item.IS_ENABLED !== 'N') {
          if (item.UI_COMP === 'DATE') {
            if (this.isUICompEmpty(item)) {
              returnObj.CELL_VALUE = '';
            } else {
              // Format date as YYYY-M-D (without leading zeros) to match original AngularJS
              returnObj.CELL_VALUE = this.formatDateForSave(item.CELL_VALUE);
            }
          } else if (item.UI_COMP === 'NUMERIC') {
            // Convert to number for NUMERIC fields, empty values should be null
            if (item.CELL_VALUE !== null && item.CELL_VALUE !== undefined && item.CELL_VALUE !== '') {
              returnObj.CELL_VALUE = Number(item.CELL_VALUE);
            } else {
              returnObj.CELL_VALUE = null; // Empty NUMERIC = null (matching original)
            }
          } else if (item.UI_COMP === 'CHK_BX') {
            // Checkboxes: true → "X", false/empty → "" (matching original AngularJS)
            returnObj.CELL_VALUE = item.CELL_VALUE === true || item.CELL_VALUE === 'X' ? 'X' : '';
          } else if (item.UI_COMP === 'DROP_DOWN') {
            // Dropdowns: empty value should be null (matching original AngularJS)
            if (item.CELL_VALUE === null || item.CELL_VALUE === undefined || item.CELL_VALUE === '') {
              returnObj.CELL_VALUE = null;
            } else {
              returnObj.CELL_VALUE = item.CELL_VALUE;
            }
          } else {
            returnObj.CELL_VALUE = item.CELL_VALUE;
          }
        }

        returnObj.OPERATION_TYPE = item.OPERATION_TYPE || '';
        
        if (!item.OPERATION_TYPE) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'I') {
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'N') {
          returnObj.OPERATION_TYPE = '';
        }

        if (returnObj.OPERATION_TYPE) {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  // ============================================
  // TRACK BY FUNCTIONS
  // ============================================

  trackByLine(index: number, line: GroupedFormLine): string {
    return `${line.LINE_KEY}_${line.SNO}_${line.IS_DISPLAY}`;
  }

  trackByItem(index: number, item: FormLineItem): string {
    return `${item.LINE_KEY}_${item.COL_KEY}_${item.SNO}`;
  }

  trackByCol(index: number, col: ColumnHeaderData): number {
    return col.COL_KEY;
  }
}
